import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/** Elements the Eyebrow primitive can render as. Constrained to the
 *  options where the kit's eyebrow visual treatment is semantically
 *  appropriate: block prose (p, div) and inline (span). An eyebrow is
 *  NOT a heading semantically — it's an overline / kicker that precedes
 *  one. Headings (h1–h6) are intentionally excluded; use <Title> for
 *  those. */
export type EyebrowAs = 'p' | 'span' | 'div';

export interface EyebrowProps
  extends
    StyledProps,
    WithChildren,
    UnsafeStyleProps,
    Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'style' | 'children'> {
  tone?: 'muted' | 'brand';
  /** Override the rendered element. Defaults to `'p'` — an eyebrow is
   *  presentational and lives as a paragraph above a heading. Pass
   *  `as="span"` for inline contexts or `as="div"` for the v4
   *  no-semantic-element behavior. */
  as?: EyebrowAs;
}

/* forwardRef so adopters can attach refs (focus management, scroll
   anchoring, intersection observers for sticky-section labels). Ref
   type is HTMLElement since `as` covers p/span/div uniformly. */
export const Eyebrow = React.forwardRef<HTMLElement, EyebrowProps>(function Eyebrow(
  { children, tone = 'muted', as = 'p', className, unsafe_style, ...rest },
  ref,
) {
  const cls = tone === 'brand' ? 'j-eyebrow-kicker' : 'j-eyebrow';
  const Element = as as React.ElementType;
  return (
    <Element ref={ref} {...rest} className={cx(cls, className)} style={unsafe_style}>
      {children}
    </Element>
  );
});
